/**
 * Voice call history + live transcript.
 *
 * The history is ordinary react-query. The live transcript is not: it
 * accumulates lines as they arrive, so it keeps its own state and polls the
 * `since` endpoint, which only ever carries what is new. Once the server says
 * the call has ended the poll stops by itself — a finished transcript is
 * static and there is nothing left to ask for.
 */
import { useIsMutating, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useState } from 'react';
import {
  acceptCallTaskSuggestion,
  deleteVoiceCall,
  dismissCallTaskSuggestion,
  emailCallMinutes,
  fetchCallMinutes,
  fetchCallTaskSuggestions,
  fetchVoiceCall,
  fetchVoiceCalls,
  fetchVoiceSegmentsSince,
  generateCallMinutes,
  generateCallTaskSuggestions,
  restoreCallTaskSuggestion,
  type VoiceTranscriptSegment,
} from '../lib/voiceCallsApi';
import type { AcceptTaskSuggestionInput, MeetingMinutes } from '../lib/meetingLogApi';

export const VOICE_CALLS_KEY = ['voice-calls'] as const;

const LIVE_POLL_MS = 4_000;

export function useVoiceCalls(input: { channelId?: string; limit?: number; live?: boolean }) {
  return useQuery({
    queryKey: [...VOICE_CALLS_KEY, 'list', input.channelId ?? null, input.limit ?? null],
    queryFn: () => fetchVoiceCalls({ channelId: input.channelId, limit: input.limit }),
    staleTime: 15_000,
    refetchOnWindowFocus: true,
    // While the reader is in a call the "recent" list shows that call as live
    // and should notice when it ends; elsewhere the history is slow-moving.
    refetchInterval: input.live ? 15_000 : false,
  });
}

const LIVE_DETAIL_REFETCH_MS = 5_000;

export function useVoiceCall(callId: string | undefined) {
  return useQuery({
    queryKey: [...VOICE_CALLS_KEY, 'detail', callId],
    queryFn: () => fetchVoiceCall(callId!),
    enabled: Boolean(callId),
    staleTime: 30_000,
    // While the call is on, who is in it changes: someone joins a minute
    // after the page opened and must appear in the participants without a
    // reload. The transcript lines have their own poll; this is for the rest.
    refetchInterval: (query) =>
      query.state.data?.endedAt === null ? LIVE_DETAIL_REFETCH_MS : false,
  });
}

export function useDeleteVoiceCall() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (callId: string) => deleteVoiceCall(callId),
    onSettled: () => qc.invalidateQueries({ queryKey: VOICE_CALLS_KEY }),
  });
}

// --- Task suggestions --------------------------------------------------------
// The AI's proposals from the call's transcript, and the decisions on them.
// Mirrors the meeting hooks in useMeetingLog.ts.

const SUGGESTIONS_KEY = (callId: string) =>
  [...VOICE_CALLS_KEY, 'task-suggestions', callId] as const;

/**
 * A call that has just ended has no suggestions yet: the backend reads the
 * transcript a couple of minutes later, so `polling` keeps the list refreshing
 * until they appear instead of asking the reader to reload.
 */
export function useCallTaskSuggestions(callId: string | undefined, polling = false) {
  return useQuery({
    queryKey: SUGGESTIONS_KEY(callId ?? ''),
    queryFn: () => fetchCallTaskSuggestions(callId as string),
    enabled: Boolean(callId),
    refetchInterval: polling ? 15_000 : false,
  });
}

function invalidateCallSuggestions(qc: ReturnType<typeof useQueryClient>, callId: string) {
  void qc.invalidateQueries({ queryKey: SUGGESTIONS_KEY(callId) });
}

export function useGenerateCallTaskSuggestions(callId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => generateCallTaskSuggestions(callId),
    onSuccess: () => invalidateCallSuggestions(qc, callId),
  });
}

export function useAcceptCallTaskSuggestion(callId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, input }: { id: string; input: AcceptTaskSuggestionInput }) =>
      acceptCallTaskSuggestion(callId, id, input),
    onSuccess: () => {
      invalidateCallSuggestions(qc, callId);
      // A real task now exists on some project.
      void qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
}

export function useDismissCallTaskSuggestion(callId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => dismissCallTaskSuggestion(callId, id),
    onSuccess: () => invalidateCallSuggestions(qc, callId),
  });
}

export function useRestoreCallTaskSuggestion(callId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => restoreCallTaskSuggestion(callId, id),
    onSuccess: () => invalidateCallSuggestions(qc, callId),
  });
}

// --- Minutes -----------------------------------------------------------------
// „Minuta discuției” of one call. Mirrors the meeting minutes hooks in
// useMeetingLog.ts; a generation is paid for, so nothing here runs on its own.

const CALL_MINUTES_KEY = (callId: string) => [...VOICE_CALLS_KEY, 'minutes', callId] as const;

export function useCallMinutes(callId: string | undefined) {
  return useQuery({
    queryKey: CALL_MINUTES_KEY(callId ?? ''),
    queryFn: () => fetchCallMinutes(callId as string),
    enabled: Boolean(callId),
  });
}

/** Shared by every „Generează minuta” button for this call. */
export const GENERATE_CALL_MINUTES_KEY = (callId: string) => ['call-minutes-generate', callId];

export function useGenerateCallMinutes(callId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationKey: GENERATE_CALL_MINUTES_KEY(callId),
    mutationFn: () => generateCallMinutes(callId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CALL_MINUTES_KEY(callId) });
    },
  });
}

/** True while this call's minutes are being written. */
export function useGeneratingCallMinutes(callId: string): boolean {
  return useIsMutating({ mutationKey: GENERATE_CALL_MINUTES_KEY(callId) }) > 0;
}

export function useEmailCallMinutes(callId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (m: MeetingMinutes) => emailCallMinutes(callId, m),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: CALL_MINUTES_KEY(callId) });
    },
  });
}

/**
 * Lines of one call, growing while it is live.
 *
 * `initial` seeds the list (the detail page already has the full transcript
 * from its first fetch); polling starts from the newest seeded line. Pass
 * `active: false` for a call known to be over and this is just state.
 */
export function useLiveTranscript(input: {
  callId: string | null;
  active: boolean;
  initial?: VoiceTranscriptSegment[];
  /**
   * Where new lines come from. Defaults to the voice-call endpoint; Firmio
   * Meet passes a fetcher bound to its participant token.
   */
  fetchSince?: (
    id: string,
    since: string | null,
  ) => Promise<{ segments: VoiceTranscriptSegment[]; ended: boolean }>;
}): { segments: VoiceTranscriptSegment[]; ended: boolean } {
  const { callId, active } = input;
  const fetchSinceRef = useRef(input.fetchSince ?? fetchVoiceSegmentsSince);
  fetchSinceRef.current = input.fetchSince ?? fetchVoiceSegmentsSince;
  const [segments, setSegments] = useState<VoiceTranscriptSegment[]>(input.initial ?? []);
  const [ended, setEnded] = useState(false);
  // The cursor lives in a ref so the interval closure always reads the newest
  // value without being re-created (and re-fired) on every batch of lines.
  const sinceRef = useRef<string | null>(null);
  const seenRef = useRef<Set<string>>(new Set());

  // A different call means a different transcript. Seed from `initial` again.
  useEffect(() => {
    const seed = input.initial ?? [];
    setSegments(seed);
    setEnded(false);
    seenRef.current = new Set(seed.map((s) => s.id));
    sinceRef.current = seed.length > 0 ? seed[seed.length - 1]!.createdAt : null;
    // `initial` is intentionally read only when the call changes: the detail
    // query refetching must not reset lines the poll has already appended.
  }, [callId]);

  useEffect(() => {
    if (!callId || !active || ended) return;
    let cancelled = false;
    let inFlight = false;

    const poll = async () => {
      if (inFlight) return;
      inFlight = true;
      try {
        const result = await fetchSinceRef.current(callId, sinceRef.current);
        if (cancelled) return;
        const fresh = result.segments.filter((s) => !seenRef.current.has(s.id));
        if (fresh.length > 0) {
          fresh.forEach((s) => seenRef.current.add(s.id));
          sinceRef.current = fresh[fresh.length - 1]!.createdAt;
          setSegments((current) =>
            [...current, ...fresh].sort(
              (a, b) =>
                a.startedAt.localeCompare(b.startedAt) || a.createdAt.localeCompare(b.createdAt),
            ),
          );
        }
        if (result.ended) setEnded(true);
      } catch {
        // A failed poll is retried on the next tick; nothing to show for it.
      } finally {
        inFlight = false;
      }
    };

    void poll();
    const timer = setInterval(() => void poll(), LIVE_POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [callId, active, ended]);

  return { segments, ended };
}
